import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, Loader2 } from 'lucide-react';
import { subscribeNewsletter } from '@/lib/api';

const SubscribeBox = ({ theme = 'dark' }) => {
  const isLight = theme === 'light';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const location = useLocation();

  const requestPushPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        toast.success('Push notifications enabled!');
        return true;
      }
    }
    return false;
  };

  const handlePushToggle = async () => {
    if (!pushEnabled) {
      await requestPushPermission();
    } else {
      setPushEnabled(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await subscribeNewsletter({ name, email, push_enabled: pushEnabled, source_page: location.pathname });
      setSubscribed(true);
      toast.success('Subscribed!');
    } catch (error) {
      toast.error('Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className={`p-6 text-center ${isLight ? 'bg-white border border-gray-200 shadow-sm' : 'bg-[#111] border border-[#222]'}`} data-testid="subscribe-success">
        <Bell className="text-yellow-600 mx-auto mb-2" size={24} />
        <p className={`font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>You're in!</p>
        <p className={`text-sm mt-1 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>We'll keep you updated.</p>
      </div>
    );
  }

  return (
    <div className={`p-4 sm:p-6 ${isLight ? 'bg-white border border-gray-200 shadow-sm' : 'bg-[#111] border border-[#222]'}`} data-testid="subscribe-box">
      <div className="text-center mb-4">
        <h3 className={`font-bold text-base ${isLight ? 'text-gray-900' : 'text-white'}`}>Stay Updated</h3>
        <p className={`text-xs mt-1 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Get the latest Cincinnati news</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className={`w-full px-3 py-2 text-sm focus:outline-none focus:border-yellow-500 ${
            isLight ? 'bg-white border border-gray-300 text-gray-900' : 'bg-black border border-[#222] text-white'
          }`}
          data-testid="subscribe-name-input"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          required
          className={`w-full px-3 py-2 text-sm focus:outline-none focus:border-yellow-500 ${
            isLight ? 'bg-white border border-gray-300 text-gray-900' : 'bg-black border border-[#222] text-white'
          }`}
          data-testid="subscribe-email-input"
        />

        {/* Push notification toggle */}
        <button
          type="button"
          onClick={handlePushToggle}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 border text-sm transition-colors ${
            pushEnabled
              ? 'border-yellow-500 text-yellow-600 bg-yellow-500/10'
              : isLight
                ? 'border-gray-300 text-gray-500 hover:border-gray-400'
                : 'border-[#222] text-gray-400 hover:border-gray-500'
          }`}
          data-testid="push-toggle-btn"
        >
          <Bell size={14} />
          {pushEnabled ? 'Push Notifications On' : 'Enable Push Notifications'}
        </button>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2 bg-yellow-600 text-white text-sm font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="subscribe-submit-btn"
        >
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
          Subscribe
        </button>
      </form>
    </div>
  );
};

export default SubscribeBox;
